// src/pages/dashboard/components/DashboardNavbar.tsx
import { motion } from 'framer-motion';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	FiMenu,
	FiUser
} from 'react-icons/fi';
import { Link, useNavigate, useParams } from 'react-router-dom';
import LanguageSwitcher from '../../../components/ui/LanguageSwitcher';
import CurrencySwitcher from '../../../components/ui/CurrencySwitcher';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { selectUser, selectOrganization } from '../../../store/selectors';
import { logoutUser } from '../../../store/slices/authSlice';

interface DashboardNavbarProps {
	onMenuClick?: () => void;
	showMenuButton?: boolean;
}

const DashboardNavbar: React.FC<DashboardNavbarProps> = ({
	onMenuClick,
	showMenuButton = false
}) => {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const { branchId } = useParams<{ branchId: string }>();
	// const { user, logout } = useAuth();

	const [userMenuOpen, setUserMenuOpen] = useState(false);
	const [notificationsOpen, setNotificationsOpen] = useState(false);

	const user = useAppSelector(selectUser)
	const organization = useAppSelector(selectOrganization).organization
	const dispatch = useAppDispatch()

	const isRTL = i18n.language === 'ar';


	const handleLogout = async () => {
		try {
			await dispatch(logoutUser()).unwrap();
			// Navigate to home page after successful logout
			navigate('/');
		} catch (error) {
			console.error('Logout failed:', error);
			// Even if logout fails, navigate to home page
			navigate('/');
		}
	};

	return (
		<div className="relative z-20 flex-shrink-0 flex h-16 bg-white dark:bg-gray-800 shadow">
			{showMenuButton && (
				<button
					className="px-4 border-e border-gray-200 dark:border-gray-700 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary md:hidden"
					onClick={onMenuClick}
				>
					<span className="sr-only">Open sidebar</span>
					<FiMenu className="h-6 w-6" />
				</button>
			)}

			<div className="flex-1 px-4 flex justify-between">
				<div className="flex items-center flex-shrink min-w-0">
					<Link to="/" className="flex md:hidden items-center pe-4">
						<span className="text-xl font-bold text-primary">Dine.ai</span>
					</Link>
					{/* <div className="w-full max-w-xs flex md:ms-0">
						<div className="relative w-full">
							<div className="pointer-events-none absolute inset-y-0 start-0 ps-3 flex items-center">
								<FiSearch className="h-5 w-5 text-gray-400" />
							</div>
							<input
								className="block w-full h-full ps-10 pe-3 py-2 rounded-md text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
								placeholder={t('topBar.search')}
								type="search"
							/>
						</div>
					</div> */}
				</div>

				<div className="ms-2 sm:ms-4 flex items-center flex-shrink-0 gap-1 sm:gap-2 md:ms-6">
					{/* Create New Button */}
					{/* <div className="me-3">
						<Button
							leftIcon={<FiPlus />}
							size="sm"
						>
							{t('common.new')}
						</Button>
					</div> */}

					{/* Language & Currency Switchers - Hidden on mobile, shown on sm+ */}
					<div className="hidden sm:block relative">
						<LanguageSwitcher />
					</div>
					{branchId && (
						<div className="hidden sm:block relative">
							<CurrencySwitcher />
						</div>
					)}

					{/* Theme Toggle */}
					{/* <button
						onClick={toggleTheme}
						className="ms-3 p-1 rounded-full text-gray-600 dark:text-gray-300 hover:text-gray-700 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary cursor-pointer"
					>
						<span className="sr-only">
							{theme === 'dark' ? t('common.lightMode') : t('common.darkMode')}
						</span>
						{theme === 'dark' ? (
							<svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
								/>
							</svg>
						) : (
							<svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
								/>
							</svg>
						)}
					</button> */}

					{/* Notification dropdown */}
					{/* <div className="ms-3 relative">
						<div>
							<button
								className="p-1 rounded-full text-gray-600 dark:text-gray-300 hover:text-gray-700 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary relative cursor-pointer"
								onClick={() => setNotificationsOpen(!notificationsOpen)}
							>
								<span className="sr-only">{t('topBar.notifications')}</span>
								<FiBell className="h-6 w-6" />
								<span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-500"></span>
							</button>
						</div>

						{notificationsOpen && (
							<motion.div
								initial={{ opacity: 0, y: -10 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: -10 }}
								transition={{ duration: 0.2 }}
								className="origin-top-right absolute right-0 mt-2 w-80 rounded-md shadow-lg py-1 bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 focus:outline-none"
							>
								<div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
									<h3 className="text-sm font-medium text-gray-900 dark:text-white">
										{t('topBar.notifications')}
									</h3>
								</div>
								<div className="max-h-64 overflow-y-auto">
								
									<div className="px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700">
										<div className="text-sm font-medium text-gray-900 dark:text-white">
											{t('notifications.newOrder')}
										</div>
										<div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
											30 {t('common.minutesAgo')}
										</div>
									</div>
								</div>
								<div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700">
									<Link
										to="/dashboard/notifications"
										className="text-sm text-primary hover:text-primary-dark"
										onClick={() => setNotificationsOpen(false)}
									>
										{t('topBar.viewAll')}
									</Link>
								</div>
							</motion.div>
						)}
					</div> */}

					{/* Profile dropdown */}
					<div className="relative">
						<div>
							<button
								className="max-w-xs bg-white dark:bg-gray-800 rounded-full flex items-center text-sm focus:outline-none focus:ring-1 focus:ring-color-orange-500 focus:ring-offset-2 focus:ring-primary cursor-pointer"
								onClick={() => setUserMenuOpen(!userMenuOpen)}
							>
								<span className="sr-only">{t('topBar.userMenu')}</span>
								<div className="h-8 w-8 rounded-full bg-orange-200 dark:bg-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300">
									<FiUser className="h-5 w-5 text-orange-400" />
								</div>
							</button>
						</div>

						{userMenuOpen && (
							<motion.div
								initial={{ opacity: 0, y: -10, scale: 0.95 }}
								animate={{ opacity: 1, y: 0, scale: 1 }}
								exit={{ opacity: 0, y: -10, scale: 0.95 }}
								transition={{ duration: 0.15 }}
								className={`origin-top-right absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-56 sm:w-52 rounded-xl shadow-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 overflow-hidden`}
							>
								<div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
									<p className="text-sm font-medium text-gray-900 dark:text-white">
										{organization?.name}
									</p>
									<p className="text-xs text-gray-500 dark:text-gray-400 truncate">
										{user?.email}
									</p>
								</div>
								{/* Mobile-only: Language & Currency in dropdown */}
								<div className="sm:hidden border-b border-gray-200 dark:border-gray-700 p-1.5">
									<div className="px-3 py-2 flex items-center justify-between rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50">
										<span className="text-sm text-gray-600 dark:text-gray-300">{t('common.language')}</span>
										<LanguageSwitcher />
									</div>
									{branchId && (
										<div className="px-3 py-2 flex items-center justify-between rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50">
											<span className="text-sm text-gray-600 dark:text-gray-300">{t('common.currency')}</span>
											<CurrencySwitcher />
										</div>
									)}
								</div>
								{/* <Link
									to="/dashboard/profile"
									className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
									onClick={() => setUserMenuOpen(false)}
								>
									{t('topBar.profile')}
								</Link>
								<Link
									to="/dashboard/settings"
									className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
									onClick={() => setUserMenuOpen(false)}
								>
									{t('topBar.settings')}
								</Link> */}
								<div className="p-1.5">
									<button
										onClick={() => {
											setUserMenuOpen(false);
											handleLogout();
										}}
										className="w-full text-start px-3 py-2.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors cursor-pointer"
									>
										{t('common.logout')}
									</button>
								</div>
							</motion.div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default DashboardNavbar;